import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, Input } from '@billda/design-system';
import { SearchIcon } from '@billda/design-system';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@billda/permissions-ui';
import { CommandPaletteResultItem } from './CommandPaletteResultItem';
import { useCommandPaletteSearch } from '../hooks/useCommandPaletteSearch';
import { useCommandPaletteKeyboard } from '../hooks/useCommandPaletteKeyboard';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  open,
  onClose,
}) => {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const permissions = usePermissions();
  const inputRef = useRef<HTMLInputElement>(null);

  // Get search results using custom hook
  const results = useCommandPaletteSearch(query, permissions);

  // Navigation handler
  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  // Handle keyboard navigation using custom hook
  const { selectedIndex, setSelectedIndex } = useCommandPaletteKeyboard(
    open,
    results,
    handleNavigate,
    onClose
  );

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setQuery('');
      // Focus input after modal animation
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <DialogContent
        data-testid="command-palette"
        aria-label="Command palette"
        className="top-[15%] max-h-[70vh] w-[calc(100%-2rem)] max-w-md translate-y-0 overflow-hidden p-0 [&>button:last-child]:hidden"
      >
        {/* Search Input */}
        <div className="p-4 pb-0">
          <Input
            ref={inputRef}
            placeholder="Search navigation..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            data-testid="command-palette-search-input"
            inputSize="lg"
            autoComplete="off"
            startAdornment={
              <SearchIcon
                aria-hidden="true"
                className="size-5 text-content-secondary"
              />
            }
          />
        </div>

        {/* Results List */}
        <div className="max-h-[calc(70vh-120px)] overflow-y-auto px-4 pb-4 pt-2">
          {results.length > 0 ? (
            <ul className="m-0 list-none p-0">
              {results.map((item, index) => (
                <CommandPaletteResultItem
                  key={item.path}
                  item={item}
                  isSelected={index === selectedIndex}
                  onClick={() => handleNavigate(item.path)}
                  onMouseEnter={() => setSelectedIndex(index)}
                />
              ))}
            </ul>
          ) : (
            <div className="py-12 text-center">
              <p className="type-body-md text-content-secondary">
                No results found
              </p>
            </div>
          )}
        </div>

        {/* Footer Hints */}
        <div className="border-t border-border-default bg-surface-raised px-4 py-3">
          <p className="type-caption flex justify-center gap-4 text-content-secondary">
            <span>Use ↑↓ to navigate</span>
            <span>•</span>
            <span>Press Enter to select</span>
            <span>•</span>
            <span>Esc to close</span>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
